import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError, API_BASE, tokenStorage } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ConfirmDialog from "../components/ConfirmDialog";
import IntermediateTestSection from "../components/IntermediateTestSection";
import CourseQuestionsTab from "../components/CourseQuestionsTab";
import CourseFinalTestTab from "../components/CourseFinalTestTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  Loader2,
  Play,
  Paperclip,
  Upload,
  Download,
  Eye,
  EyeOff,
  Pencil,
  FileVideo,
  VideoOff,
  AlertTriangle,
  ImagePlus,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
const LEVELS = [
  { value: "base", label: "Base" },
  { value: "intermedio", label: "Intermedio" },
  { value: "avanzato", label: "Avanzato" },
];

function formatBytes(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

/** Caricamento del file video della lezione, con avanzamento e sostituzione. */
function LessonVideoUpload({ lesson, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp4")) {
      toast.error("Sono accettati solo file MP4.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setPct(0);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success("Video caricato");
      onChanged({ ...lesson, has_video: true, video_filename: data.video_filename, video_size: data.video_size });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setPct(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeVideo = async () => {
    try {
      await api.delete(`/lessons/${lesson.id}/video`);
      toast.success("Video rimosso");
      onChanged({ ...lesson, has_video: false, video_filename: null, video_size: null });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,.mp4"
        className="hidden"
        data-testid={`lesson-video-input-${lesson.id}`}
        onChange={(e) => upload(e.target.files?.[0])}
      />

      {uploading ? (
        <div data-testid={`lesson-video-progress-${lesson.id}`}>
          <div className="flex items-center justify-between text-xs text-brand-muted mb-1">
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Caricamento…
            </span>
            <strong className="text-brand-blue">{pct}%</strong>
          </div>
          <div className="h-1.5 bg-brand-line rounded-sm overflow-hidden">
            <div className="h-full bg-brand-blue transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`lesson-video-upload-${lesson.id}`}
            className="inline-flex items-center gap-1.5 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-3 py-1.5 text-sm font-semibold"
          >
            <Upload size={14} /> {lesson.has_video ? "Sostituisci video" : "Carica video MP4"}
          </button>
          {lesson.has_video && (
            <button
              type="button"
              onClick={removeVideo}
              data-testid={`lesson-video-delete-${lesson.id}`}
              className="inline-flex items-center gap-1.5 rounded-sm border border-brand-line text-brand-muted hover:border-red-500 hover:text-red-600 px-3 py-1.5 text-sm"
            >
              <Trash2 size={14} /> Rimuovi
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LessonEditor({ lesson, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: lesson.title, description: lesson.description || "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ title: lesson.title, description: lesson.description || "" });
  }, [lesson]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lessons/${lesson.id}`, form);
      onSaved(data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`lesson-${lesson.id}`} className="border border-brand-line rounded-sm p-4 bg-brand-bg">
      <div className="grid md:grid-cols-[220px_1fr] gap-4">
        <div className="aspect-video bg-brand-ink rounded-sm overflow-hidden flex flex-col items-center justify-center text-center px-3">
          {lesson.has_video ? (
            <>
              <FileVideo size={26} className="text-brand-gold mb-1.5" />
              <div className="text-[11px] text-white/90 truncate max-w-full">{lesson.video_filename}</div>
              <div className="text-[10px] text-white/60">{formatBytes(lesson.video_size)}</div>
            </>
          ) : (
            <>
              <VideoOff size={26} className="text-brand-muted mb-1.5" />
              <div className="text-[11px] text-brand-muted">Nessun video caricato</div>
            </>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-3">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 rounded-sm" />
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full min-h-[70px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Descrizione lezione (opzionale)"
              />
              <div className="flex gap-2">
                <button onClick={save} disabled={saving} data-testid={`lesson-save-${lesson.id}`} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                  {saving && <Loader2 size={14} className="animate-spin" />} Salva
                </button>
                <button type="button" onClick={() => setEditing(false)} className="px-3 py-2 text-sm text-brand-muted">Annulla</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-brand-gold">Lezione</div>
                  <div className="text-base font-semibold text-brand-ink truncate">{lesson.title}</div>
                  {!lesson.has_video && (
                    <div className="text-xs text-amber-700 inline-flex items-center gap-1 mt-0.5">
                      <AlertTriangle size={12} /> Video mancante: gli studenti non possono seguire la lezione
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  <button title="Modifica" onClick={() => setEditing(true)} data-testid={`lesson-edit-${lesson.id}`} className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted">
                    <Pencil size={14} />
                  </button>
                  <button title="Elimina" onClick={onDeleted} data-testid={`lesson-delete-${lesson.id}`} className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {lesson.description && <p className="text-sm text-brand-muted mt-2">{lesson.description}</p>}
              <div className="mt-3">
                <LessonVideoUpload lesson={lesson} onChanged={onSaved} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModuleBlock({ module, index, total, onMove, onDeleted, onUpdated, questions }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: module.title, description: module.description || "" });
  const [saving, setSaving] = useState(false);

  const [lessons, setLessons] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  const [newLesson, setNewLesson] = useState({ title: "", description: "" });
  const [creatingLesson, setCreatingLesson] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  const loadContent = useCallback(async () => {
    setLoadingContent(true);
    try {
      const [lessRes, attRes] = await Promise.all([
        api.get(`/modules/${module.id}/lessons`),
        api.get(`/modules/${module.id}/attachments`),
      ]);
      setLessons(lessRes.data);
      setAttachments(attRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingContent(false);
    }
  }, [module.id]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const saveModule = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/modules/${module.id}`, form);
      onUpdated(data);
      setEditing(false);
      toast.success("Modulo aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addLesson = async (e) => {
    e.preventDefault();
    setCreatingLesson(true);
    try {
      const { data } = await api.post(`/modules/${module.id}/lessons`, newLesson);
      setLessons((prev) => [...prev, data]);
      setNewLesson({ title: "", description: "" });
      toast.success("Lezione aggiunta");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingLesson(false);
    }
  };

  const deleteLesson = async (lesson) => {
    if (!window.confirm(`Eliminare la lezione "${lesson.title}"?`)) return;
    try {
      await api.delete(`/lessons/${lesson.id}`);
      setLessons((prev) => prev.filter((l) => l.id !== lesson.id));
      toast.success("Lezione eliminata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveLesson = async (idx, dir) => {
    const next = [...lessons];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setLessons(next);
    try {
      await api.post(`/modules/${module.id}/lessons/reorder`, { order: next.map((l) => l.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      loadContent();
    }
  };

  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/modules/${module.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [data, ...prev]);
      toast.success("PDF caricato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await fetch(`${API_BASE}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${tokenStorage.get()}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Errore download");
    }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Eliminare l'allegato "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success("Allegato eliminato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`module-${module.id}`} className="bg-white border border-brand-line rounded-sm">
      <div className="flex items-start justify-between gap-3 p-5 border-b border-brand-line">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="flex flex-col gap-1 shrink-0">
            <button onClick={() => onMove(index, -1)} disabled={index === 0} data-testid={`module-up-${module.id}`} className="p-1 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronUp size={14} />
            </button>
            <button onClick={() => onMove(index, 1)} disabled={index === total - 1} data-testid={`module-down-${module.id}`} className="p-1 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronDown size={14} />
            </button>
          </div>
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="space-y-2">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 rounded-sm" />
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full min-h-[60px] rounded-sm border border-brand-line px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <button onClick={saveModule} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
                    {saving && <Loader2 size={14} className="animate-spin" />} Salva
                  </button>
                  <button onClick={() => setEditing(false)} className="px-3 py-2 text-sm text-brand-muted">Annulla</button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-widest text-brand-gold">Modulo {index + 1}</div>
                <h3 className="text-lg text-brand-blue font-semibold">{module.title}</h3>
                {module.description && <p className="text-sm text-brand-muted mt-1">{module.description}</p>}
              </>
            )}
          </div>
        </div>
        {!editing && (
          <div className="flex gap-1 shrink-0">
            <button title="Modifica" onClick={() => setEditing(true)} data-testid={`module-edit-${module.id}`} className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted">
              <Pencil size={14} />
            </button>
            <button title="Elimina modulo" onClick={onDeleted} data-testid={`module-delete-${module.id}`} className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Lessons */}
        <div>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-brand-ink mb-3">
            <Play size={14} className="text-brand-blue" /> Lezioni ({lessons.length})
          </h4>
          {loadingContent ? (
            <div className="text-sm text-brand-muted"><Loader2 size={14} className="inline animate-spin mr-2" /> Caricamento…</div>
          ) : lessons.length === 0 ? (
            <div className="text-sm text-brand-muted bg-brand-bg border border-dashed border-brand-line rounded-sm p-4">Nessuna lezione. Aggiungine una qui sotto.</div>
          ) : (
            <div className="space-y-3">
              {lessons.map((l, i) => (
                <div key={l.id} className="flex gap-2 items-start">
                  <div className="flex flex-col gap-1 shrink-0 mt-2">
                    <button onClick={() => moveLesson(i, -1)} disabled={i === 0} data-testid={`lesson-up-${l.id}`} className="p-1 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted disabled:opacity-30 disabled:cursor-not-allowed">
                      <ChevronUp size={12} />
                    </button>
                    <button onClick={() => moveLesson(i, 1)} disabled={i === lessons.length - 1} data-testid={`lesson-down-${l.id}`} className="p-1 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted disabled:opacity-30 disabled:cursor-not-allowed">
                      <ChevronDown size={12} />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <LessonEditor
                      lesson={l}
                      onSaved={(updated) => setLessons((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
                      onDeleted={() => deleteLesson(l)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={addLesson} data-testid={`add-lesson-form-${module.id}`} className="mt-4 border-t border-dashed border-brand-line pt-4 grid md:grid-cols-[1fr_auto] gap-2">
            {/* Il video si carica dopo aver creato la lezione, dal riquadro della lezione stessa */}
            <Input
              data-testid={`new-lesson-title-${module.id}`}
              value={newLesson.title}
              onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
              placeholder="Titolo lezione"
              className="h-10 rounded-sm"
              required
            />
            <button
              type="submit"
              disabled={creatingLesson}
              data-testid={`add-lesson-submit-${module.id}`}
              className="btn-primary inline-flex items-center gap-1.5 h-10 disabled:opacity-60"
            >
              {creatingLesson ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Aggiungi
            </button>
          </form>
        </div>

        {/* Attachments */}
        <div>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-brand-ink mb-3">
            <Paperclip size={14} className="text-brand-blue" /> Allegati PDF ({attachments.length})
          </h4>
          {attachments.length === 0 ? (
            <div className="text-sm text-brand-muted bg-brand-bg border border-dashed border-brand-line rounded-sm p-4">Nessun PDF allegato.</div>
          ) : (
            <div className="space-y-2">
              {attachments.map((att) => (
                <div key={att.id} data-testid={`attachment-${att.id}`} className="flex items-center justify-between gap-3 border border-brand-line rounded-sm p-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip size={16} className="text-brand-blue shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-brand-ink truncate">{att.filename}</div>
                      <div className="text-xs text-brand-muted">{Math.round(att.size / 1024)} KB</div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => downloadAttachment(att)} data-testid={`attachment-download-${att.id}`} title="Scarica" className="p-1.5 rounded-sm border border-brand-line hover:border-brand-blue hover:text-brand-blue text-brand-muted">
                      <Download size={14} />
                    </button>
                    <button onClick={() => deleteAttachment(att)} data-testid={`attachment-delete-${att.id}`} title="Elimina" className="p-1.5 rounded-sm border border-brand-line hover:border-red-500 hover:text-red-600 text-brand-muted">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <label className="inline-flex items-center gap-2 rounded-sm border border-dashed border-brand-blue/50 text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm cursor-pointer">
              {uploadingFile ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Carica PDF (max 20 MB)
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFileUpload}
                className="hidden"
                data-testid={`upload-attachment-${module.id}`}
              />
            </label>
          </div>
        </div>

        {/* Test del modulo: senza questo blocco i test si potevano configurare
            solo via API. Le domande arrivano dal pool del corso, già caricato
            dalla pagina: nessuna chiamata aggiuntiva. */}
        <IntermediateTestSection moduleId={module.id} courseQuestions={questions} />
      </div>
    </div>
  );
}

/** Logo azienda del corso: caricamento/sostituzione/rimozione + anteprima.
 * Compare sull'attestato, in alto a destra, in dimensioni fisse. */
function CompanyLogoField({ course, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Sono accettati solo file PNG o JPEG.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/courses/${course.id}/company-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo caricato");
      setBust(Date.now());
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    try {
      const { data } = await api.delete(`/courses/${course.id}/company-logo`);
      toast.success("Logo rimosso");
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="md:col-span-2">
      <Label>Logo azienda (mostrato sull'attestato)</Label>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        data-testid="course-logo-input"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <div className="mt-1.5 flex items-center gap-4 border border-brand-line rounded-sm p-3 bg-brand-bg">
        <div className="h-16 w-40 shrink-0 bg-white border border-brand-line rounded-sm flex items-center justify-center overflow-hidden">
          {course.has_company_logo ? (
            <img
              src={`${API_BASE}/courses/${course.id}/company-logo?v=${bust}`}
              alt="Logo azienda"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="text-xs text-brand-muted">Nessun logo</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="course-logo-upload-btn"
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
              {course.has_company_logo ? "Sostituisci" : "Carica logo"}
            </button>
            {course.has_company_logo && (
              <button
                type="button"
                onClick={remove}
                data-testid="course-logo-remove-btn"
                className="text-sm text-red-600 hover:underline px-2"
              >
                Rimuovi
              </button>
            )}
          </div>
          <div className="text-[11px] text-brand-muted mt-1.5">
            PNG o JPEG, max 3 MB. Comparirà sull'attestato in alto a destra, a fianco del logo Buro Academy, in dimensioni fisse. Consigliato PNG con sfondo trasparente.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [creatingModule, setCreatingModule] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteModule, setConfirmDeleteModule] = useState(null);
  const [areaOptions, setAreaOptions] = useState([]);

  // Aree già in uso: suggerite in fase di modifica; se ne scrivi una nuova viene creata.
  useEffect(() => {
    api.get("/courses/areas").then((r) => setAreaOptions(r.data || [])).catch(() => {});
  }, []);

  const reloadQuestions = useCallback(async () => {
    try {
      const { data } = await api.get(`/courses/${id}/questions`);
      setQuestions(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, mRes, qRes] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get(`/courses/${id}/modules`),
        api.get(`/courses/${id}/questions`),
      ]);
      setCourse(cRes.data);
      setModules(mRes.data);
      setQuestions(qRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate(user?.role === "super_admin" ? "/admin/corsi" : "/formatore");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, user]);

  useEffect(() => { load(); }, [load]);

  const saveCourseMeta = async () => {
    setSaving(true);
    try {
      const payload = {
        title: course.title,
        description: course.description,
        short_description: course.short_description || null,
        area: course.area,
        level: course.level || "base",
        duration_hours:
          course.duration_hours === "" || course.duration_hours == null
            ? null
            : Number(course.duration_hours),
        price:
          course.price === "" || course.price == null
            ? 0
            : Number(course.price),
        ai_generated_content: !!course.ai_generated_content,
      };
      if (user?.role === "super_admin") {
        payload.is_catalog_visible = !!course.is_catalog_visible;
      }
      const { data } = await api.patch(`/courses/${id}`, payload);
      setCourse(data);
      toast.success("Corso aggiornato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    try {
      const endpoint = course.is_published ? "unpublish" : "publish";
      const { data } = await api.post(`/courses/${id}/${endpoint}`);
      setCourse(data);
      toast.success(data.is_published ? "Corso pubblicato" : "Corso spostato in bozza");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteCourse = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Corso eliminato");
      navigate(user?.role === "super_admin" ? "/admin/corsi" : "/formatore");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addModule = async (e) => {
    e.preventDefault();
    if (!newModuleTitle.trim()) return;
    setCreatingModule(true);
    try {
      const { data } = await api.post(`/courses/${id}/modules`, { title: newModuleTitle });
      setModules((prev) => [...prev, data]);
      setNewModuleTitle("");
      toast.success("Modulo aggiunto");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingModule(false);
    }
  };

  const moveModule = async (idx, dir) => {
    const next = [...modules];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setModules(next);
    try {
      await api.post(`/courses/${id}/modules/reorder`, { order: next.map((m) => m.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const deleteModule = async () => {
    if (!confirmDeleteModule) return;
    try {
      await api.delete(`/modules/${confirmDeleteModule.id}`);
      setModules((prev) => prev.filter((m) => m.id !== confirmDeleteModule.id));
      toast.success("Modulo eliminato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDeleteModule(null);
    }
  };

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="text-center py-16 text-brand-muted">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento corso…
        </div>
      </div>
    );
  }

  const backTo = user?.role === "super_admin" ? "/admin/corsi" : "/formatore";

  return (
    <div data-testid="course-editor-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(backTo)}
          data-testid="course-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Torna ai corsi
        </button>

        {/* Course meta card */}
        <div className="bg-white border border-brand-line rounded-sm p-6 sm:p-8 mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
                Editor corso
              </div>
              <h1 className="text-2xl sm:text-3xl text-brand-ink" data-testid="course-title-display">
                {course.title}
              </h1>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate(`/corsi/${id}/progressi`)}
                data-testid="course-progress-link"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-semibold text-sm border border-brand-blue/30 text-brand-blue hover:bg-brand-blue/5"
              >
                Progressi studenti
              </button>
              <button
                onClick={togglePublish}
                data-testid="course-publish-btn"
                className={
                  "inline-flex items-center gap-2 px-4 py-2 rounded-sm font-semibold text-sm border transition-colors " +
                  (course.is_published
                    ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                    : "border-emerald-300 text-emerald-700 hover:bg-emerald-50")
                }
              >
                {course.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                {course.is_published ? "Sposta in bozza" : "Pubblica"}
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="course-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm font-semibold text-sm border border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 size={14} /> Elimina
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Titolo</Label>
              <Input
                data-testid="course-title-input"
                value={course.title}
                onChange={(e) => setCourse({ ...course, title: e.target.value })}
                className="mt-1.5 h-10 rounded-sm"
              />
            </div>
            <div>
              <Label>Area</Label>
              <Input
                data-testid="course-area-input"
                list="course-area-options"
                value={course.area || ""}
                onChange={(e) => setCourse({ ...course, area: e.target.value })}
                className="mt-1.5 h-10 rounded-sm"
                placeholder="Scegli un'area o scrivine una nuova"
                maxLength={60}
              />
              <datalist id="course-area-options">
                {areaOptions.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
              <div className="text-[11px] text-brand-muted mt-1">
                Digita un'etichetta nuova per creare un'area; le aree esistenti sono suggerite.
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Descrizione</Label>
              <textarea
                data-testid="course-description-input"
                value={course.description || ""}
                onChange={(e) => setCourse({ ...course, description: e.target.value })}
                className="mt-1.5 w-full min-h-[90px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Descrizione breve (catalogo pubblico, max 300 caratteri)</Label>
              <textarea
                data-testid="course-short-description-input"
                value={course.short_description || ""}
                maxLength={300}
                onChange={(e) => setCourse({ ...course, short_description: e.target.value })}
                className="mt-1.5 w-full min-h-[60px] rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Riassunto usato nella card del catalogo pubblico"
              />
              <div className="text-[11px] text-brand-muted mt-1">
                {(course.short_description || "").length}/300 · se vuoto, verrà mostrato l'inizio della descrizione completa.
              </div>
            </div>
            <div>
              <Label>Livello</Label>
              <Select value={course.level || "base"} onValueChange={(v) => setCourse({ ...course, level: v })}>
                <SelectTrigger data-testid="course-level-select" className="mt-1.5 h-10 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durata (ore)</Label>
              <Input
                data-testid="course-duration-input"
                type="number"
                min={0}
                value={course.duration_hours ?? ""}
                onChange={(e) => setCourse({ ...course, duration_hours: e.target.value })}
                placeholder="es. 8"
                className="mt-1.5 h-10 rounded-sm"
              />
            </div>
            <div>
              <Label>Prezzo base per addetto (€ IVA esclusa)</Label>
              <Input
                data-testid="course-price-input"
                type="number"
                min={0}
                step="0.01"
                value={course.price ?? 0}
                onChange={(e) => setCourse({ ...course, price: e.target.value })}
                className="mt-1.5 h-10 rounded-sm"
              />
              <div className="text-[11px] text-brand-muted mt-1">
                Sconti: 2° addetto −10%, dal 3° −20% (calcolati nel modulo PDF).
              </div>
            </div>
            <CompanyLogoField
              course={course}
              onChanged={(data) => setCourse((p) => ({ ...p, has_company_logo: data.has_company_logo }))}
            />
            <div className="md:col-span-2">
              <label
                data-testid="course-ai-content-label"
                className="flex items-start gap-3 cursor-pointer bg-brand-bg border border-brand-line rounded-sm p-4 hover:border-brand-blue/40 transition-colors"
              >
                <input
                  data-testid="course-ai-content-toggle"
                  type="checkbox"
                  checked={!!course.ai_generated_content}
                  onChange={(e) => setCourse({ ...course, ai_generated_content: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-brand-blue"
                />
                <div>
                  <div className="text-sm font-semibold text-brand-ink">
                    Presenti contenuti generati con IA
                  </div>
                  <div className="text-xs text-brand-muted mt-0.5">
                    Se attivo, nel catalogo pubblico su <code>/corsi</code> il corso mostrerà la dicitura «Presenti contenuti generati con IA».
                  </div>
                </div>
              </label>
            </div>
            {user?.role === "super_admin" && (
              <div className="md:col-span-2">
                <label
                  data-testid="course-catalog-visible-label"
                  className="flex items-start gap-3 cursor-pointer bg-brand-bg border border-brand-line rounded-sm p-4 hover:border-brand-blue/40 transition-colors"
                >
                  <input
                    data-testid="course-catalog-visible-toggle"
                    type="checkbox"
                    checked={!!course.is_catalog_visible}
                    onChange={(e) => setCourse({ ...course, is_catalog_visible: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-brand-blue"
                  />
                  <div>
                    <div className="text-sm font-semibold text-brand-ink">
                      Visibile nel catalogo pubblico
                    </div>
                    <div className="text-xs text-brand-muted mt-0.5">
                      Se attivo, il corso appare nel catalogo pubblico su <code>/corsi</code> e permette il download del modulo di iscrizione. Indipendente dallo stato di pubblicazione.
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={saveCourseMeta}
              disabled={saving}
              data-testid="course-save-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salva dettagli
            </button>
          </div>
        </div>

        {/* Tabs: Struttura, Domande, Test finale */}
        <Tabs defaultValue="struttura" className="w-full">
          <TabsList data-testid="editor-tabs" className="mb-6 rounded-sm bg-brand-bg border border-brand-line">
            <TabsTrigger data-testid="tab-struttura" value="struttura" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-blue data-[state=active]:shadow-sm">
              Struttura corso
            </TabsTrigger>
            <TabsTrigger data-testid="tab-domande" value="domande" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-blue data-[state=active]:shadow-sm">
              Pool domande <span className="ml-1.5 text-xs bg-brand-blue/10 text-brand-blue rounded-sm px-1.5">{questions.length}</span>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-test-finale" value="test-finale" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-blue data-[state=active]:shadow-sm">
              Test finale
            </TabsTrigger>
          </TabsList>

          <TabsContent value="struttura">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <h2 className="text-xl text-brand-blue">Moduli del corso</h2>
                <p className="text-sm text-brand-muted">{modules.length} modulo/i. Ordina con le frecce.</p>
              </div>
            </div>

            <div className="space-y-6 mb-8">
              {modules.map((m, i) => (
                <ModuleBlock
                  key={m.id}
                  module={m}
                  index={i}
                  total={modules.length}
                  onMove={moveModule}
                  onDeleted={() => setConfirmDeleteModule(m)}
                  onUpdated={(updated) => setModules((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
                  questions={questions}
                />
              ))}
            </div>

            {/* Add module */}
            <form onSubmit={addModule} data-testid="add-module-form" className="bg-white border border-dashed border-brand-blue/40 rounded-sm p-5 flex flex-wrap gap-2 items-center">
              <Input
                data-testid="new-module-title"
                value={newModuleTitle}
                onChange={(e) => setNewModuleTitle(e.target.value)}
                placeholder="Titolo del nuovo modulo…"
                className="flex-1 min-w-[240px] h-10 rounded-sm"
                required
              />
              <button
                type="submit"
                disabled={creatingModule}
                data-testid="add-module-submit"
                className="btn-primary inline-flex items-center gap-2 h-10 disabled:opacity-60"
              >
                {creatingModule ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Aggiungi modulo
              </button>
            </form>
          </TabsContent>

          <TabsContent value="domande">
            <CourseQuestionsTab courseId={id} questions={questions} reload={reloadQuestions} />
          </TabsContent>

          <TabsContent value="test-finale">
            <CourseFinalTestTab courseId={id} questions={questions} />
          </TabsContent>
        </Tabs>
      </main>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminare il corso?"
        description="L’operazione è definitiva ed elimina anche moduli, lezioni e allegati PDF collegati."
        confirmLabel="Elimina corso"
        destructive
        onConfirm={deleteCourse}
        testid="confirm-delete-course"
      />
      <ConfirmDialog
        open={!!confirmDeleteModule}
        onOpenChange={(v) => !v && setConfirmDeleteModule(null)}
        title="Eliminare il modulo?"
        description={`Sarà rimosso "${confirmDeleteModule?.title || ""}" con tutte le lezioni e gli allegati.`}
        confirmLabel="Elimina modulo"
        destructive
        onConfirm={deleteModule}
        testid="confirm-delete-module"
      />
    </div>
  );
}
